import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { modelsApi } from '../services/api';
import type { PlatformModel } from '../types';

export function useModels() {
  return useQuery({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 1000 * 60 * 10, // Models change rarely — 10 min cache
  });
}

// Bedrock cross-region inference-profile prefixes an agent's modelId may carry.
const PROFILE_PREFIXES = ['us-gov', 'us', 'eu', 'apac'] as const;

/**
 * Strips a leading Bedrock inference-profile region prefix (e.g. "us.", "eu.",
 * "apac.", "us-gov.") so lookups work whether an agent stored a bare or
 * profile-prefixed model id.
 */
export function stripProfilePrefix(modelId: string): string {
  return modelId.replace(/^(us-gov|us|eu|apac)\./, '');
}

/**
 * Returns a map from modelId → PlatformModel.
 * Indexes by both bare providerModelId and the profile-prefixed variant
 * so lookups work for both new and legacy agents.
 */
export function useModelLookup(): Record<string, PlatformModel> {
  const { data: models } = useModels();
  return useMemo(() => {
    if (!models) return {};
    const map: Record<string, PlatformModel> = {};
    for (const m of models) {
      // The catalog stores bare ids; legacy agents may store an inference-prefixed
      // id (e.g. us.anthropic.claude-sonnet-4-6), so index every prefixed variant.
      map[m.providerModelId] = m;
      for (const prefix of PROFILE_PREFIXES) {
        map[`${prefix}.${m.providerModelId}`] = m;
      }
    }
    return map;
  }, [models]);
}
